import { type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { SectionCard } from "#src/commonV2/SectionCard";
import paymentService from "#src/services/payment";
import { ButtonV2 } from "#src/commonV2/Button";
import { parseBadRequest, apiErrorHandler } from "#src/utils/apiErrorHandler";
import { AxiosError } from "axios";
import { z } from "zod";
import { createToast } from "#src/common/system/toasts";
import { useCustomerInfoForm } from "#src/components/payment/v2/EnterInformation/CustomerInfo/useCustomerInfoForm";
import { FormInput } from "#src/commonV2/form/Input/FormInput";
import { FormDropdown } from "#src/commonV2/form/Dropdown/FormDropdown";

interface IBillingConfigCustomerProps {
  workspaceId: string;
  defaultPayment: Awaited<
    ReturnType<typeof paymentService.retrieveWorkspacePaymentSettings>
  > | null;
  setDefaultPayment: Dispatch<
    SetStateAction<
      Awaited<
        ReturnType<typeof paymentService.retrieveWorkspacePaymentSettings>
      >
    >
  >;
}

export const BillingConfigCustomer = ({
  workspaceId,
  defaultPayment,
  setDefaultPayment,
}: IBillingConfigCustomerProps) => {
  const { t } = useTranslation("workspace", {
    keyPrefix: "billing.tab.config.customerInfo",
  });
  const { t: tNotification } = useTranslation("workspace", {
    keyPrefix: "billing.tab.config.notification",
  });

  // --------------- INITIALIZATION ---------------

  const {
    value: customerInfo,
    wasChanged: customerInfoWasChanged,
    resetForm: resetCustomerInfo,
    hasError: customerInfoHasError,
    setExternalError: setCustomerInfoExternalError,
    renderProps: customerInfoRenderProps,
  } = useCustomerInfoForm(defaultPayment);

  // --------------- METHODS ---------------

  const onSavePaymentSettings = () => {
    if (defaultPayment) {
      if (customerInfoHasError) {
        createToast({
          type: "error",
          message: tNotification("updateInfo.fail.message"),
          detail: tNotification("updateInfo.fail.detail"),
        });
        return;
      }
      paymentService
        .updateWorkspacePaymentSettings(workspaceId, {
          ...defaultPayment,
          customer_full_name: customerInfo.fullName,
          customer_phone_number: customerInfo.phoneNumber,
          customer_job_title: customerInfo.jobTitle,
          customer_email: customerInfo.email,
        })
        .then((response) => {
          setDefaultPayment(response);
          resetCustomerInfo();
          createToast({
            type: "success",
            message: tNotification("updateInfo.success.message"),
            detail: tNotification("updateInfo.success.detail"),
          });
        })
        .catch((error) => {
          if (
            error instanceof AxiosError &&
            error.response &&
            error.response.status === 400
          ) {
            const getErrorData = parseBadRequest(
              error.response.data,
              z.object({
                customer_full_name: z.optional(z.array(z.string())),
                customer_phone_number: z.optional(z.array(z.string())),
                customer_job_title: z.optional(z.array(z.string())),
                customer_email: z.optional(z.array(z.string())),
              })
            );
            if (getErrorData.details.customer_full_name) {
              setCustomerInfoExternalError.fullName(
                getErrorData.details.customer_full_name[0]
              );
            }
            if (getErrorData.details.customer_phone_number) {
              setCustomerInfoExternalError.phoneNumber(
                getErrorData.details.customer_phone_number[0]
              );
            }
            if (getErrorData.details.customer_email) {
              setCustomerInfoExternalError.email(
                getErrorData.details.customer_email[0]
              );
            }
            createToast({
              type: "error",
              message: tNotification("updateInfo.fail.message"),
              detail: tNotification("updateInfo.fail.detail"),
            });
          } else {
            apiErrorHandler(error);
          }
        });
    }
  };

  // --------------- RENDER ---------------

  return (
    <>
      <SectionCard
        header={
          <div className="flex items-center justify-between gap-4">
            {t("title")}
            <div className="flex items-center gap-1">
              <ButtonV2
                variant="secondary"
                color="gray"
                size="sm"
                className={customerInfoWasChanged ? "" : "invisible"}
                onClick={resetCustomerInfo}
              >
                {t("button.cancel")}
              </ButtonV2>
              <ButtonV2
                variant="secondary"
                size="sm"
                className={customerInfoWasChanged ? "" : "invisible"}
                onClick={onSavePaymentSettings}
              >
                {t("button.save")}
              </ButtonV2>
            </div>
          </div>
        }
        className="h-fit"
      >
        <div className="p-4 flex flex-col gap-4 rounded-xl border border-gray-v2-200 bg-white">
          <span className="text-md text-gray-v2-600">{t("info")}</span>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <FormInput {...customerInfoRenderProps.fullName} />
            <FormInput {...customerInfoRenderProps.phoneNumber} />
            <FormDropdown {...customerInfoRenderProps.jobTitle} />
            <FormInput {...customerInfoRenderProps.email} />
          </div>
        </div>
      </SectionCard>
    </>
  );
};
